import { Checkbox, FormControl, FormControlLabel, Grid, Typography } from '@mui/material';
import { FC } from 'react';
import { TelephoneBoards } from '../../../components/NavDrawer/NavDrawerModels';
import { ITheme } from '../../../utils/theme';
import { IOption, ISquamishSubBoard, IToggle } from '../TelephonePlugInData';
import { SubBoardsContainerStyle, TitleStyle } from './styled';
import { TooltipDecorator } from './TooltipDecorator';

export interface IPlugInOptionsAndTogglesProps {
  options: IOption[],
  toggles: IToggle[],
  onChangeOption: (e: React.ChangeEvent<HTMLInputElement>, code: string) => void;
  onChangeToggle: (code: string, t: IToggle) => void;
  disabledOptions: boolean;
  board?: TelephoneBoards,
  hasExceedDespatchCutoffTime: boolean,
}

export const PlugInOptionsAndToggles: FC<IPlugInOptionsAndTogglesProps> = ({
  options,
  toggles,
  onChangeOption,
  onChangeToggle,
  disabledOptions,
  board,
  hasExceedDespatchCutoffTime
}) => {
  const subBoards = toggles.find(t => t.squamishSubBoards)?.squamishSubBoards || [];
  const checkedSubBoards = subBoards?.filter(sb => sb.isChecked);
  const checkedSubBoardsSum = subBoards ? subBoards.filter(sb => sb.isChecked).length : 0;
  const checkedMaxSubBoards = checkedSubBoardsSum === 3;

  const tooltipTitle = disabledOptions && hasExceedDespatchCutoffTime ?
    "Plugin for this shift is over" : "You must plugin before choosing Plugin Options";
    
  const getUpdateSequence = (selectedItemIndex: number, isChecked: boolean): ISquamishSubBoard[] => {
    //...JSON.parse(JSON.stringify(obj)) to deep clone object
    const updatedSequence = [...JSON.parse(JSON.stringify(subBoards))];
    updatedSequence[selectedItemIndex].isChecked = isChecked;
    if (isChecked) {
      // Find the maximum sequence number in the updatedSequence
      let maxSequenceNumber = 0;
      for (const item of updatedSequence) {
        if (item.isChecked && item.sequence) {
          const itemSequenceNumber = parseInt(item.sequence.substring(1), 10);
          if (!isNaN(itemSequenceNumber) && itemSequenceNumber > maxSequenceNumber) {
            maxSequenceNumber = itemSequenceNumber;
          }
        }
      }
      // Assign the next available sequence number
      updatedSequence[selectedItemIndex].sequence = `[${maxSequenceNumber + 1}]`;
    } else {
      // If the item is unchecked, clear its sequence value
      updatedSequence[selectedItemIndex].sequence = "";
      // Update the sequence numbers of other checked items
      const checkedItems = updatedSequence.filter((item) => item.isChecked && item.sequence);

      checkedItems.sort((a, b) => {
        const seqA = parseInt(a.sequence.substring(1), 10);
        const seqB = parseInt(b.sequence.substring(1), 10);
        return seqA - seqB;
      });

      for (let i = 0; i < checkedItems.length; i++) {
        checkedItems[i].sequence = `[${i + 1}]`;
      }
    }
    return updatedSequence;
  }

  return (
    <Grid container sx={(theme: ITheme) => ({ borderTop: `1px solid ${theme.palette.grey.A350}` })}>
      <Grid item xs={1} sm={2} />
      <Grid item xs={10} sm={8} sx={{ p: 0 }}>
        <Typography sx={TitleStyle}>Plug-In Options:</Typography>
        {
          options.map(o => (
            <FormControl sx={{ display: 'block' }} key={o.code}>
              <TooltipDecorator title={tooltipTitle} showTooltip={disabledOptions}>
                <FormControlLabel
                  sx={{ ml: 2 }}
                  control={<Checkbox checked={o.isChecked} onChange={(e) => onChangeOption(e, o.code)} />}
                  label={o.description}
                  disabled={disabledOptions}
                />
              </TooltipDecorator>
            </FormControl>
          ))
        }
        <Typography sx={TitleStyle}>Plug-In Toggles:</Typography>
        {
          toggles.map(t => (
            <FormControl sx={{ display: 'block' }} key={t.code}>
              <TooltipDecorator
                title={`${t.description} Toggle for this shift will be available after ${t.expireDateTime}`}
                showTooltip={!t.toggleCodeEnabled}
              >
                <FormControlLabel
                  sx={{ ml: 2 }}
                  control={<Checkbox
                    checked={t.toggleCodeChecked}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.stopPropagation();

                      onChangeToggle(t.code, {
                        ...t,
                        toggleCodeChecked: e.target.checked,
                        squamishSubBoards: toggles.find(ot =>
                          ot.code === t.code)?.squamishSubBoards?.map(sb =>
                            e.target.checked ? sb : ({ ...sb, isChecked: false, sequence: '' })) || null,
                      })
                    }}
                  />}
                  label={t.description}
                  disabled={!t.toggleCodeEnabled}
                />
              </TooltipDecorator>
              {board === TelephoneBoards.Squamish && t.squamishSubBoards && t.toggleCodeChecked ?
                <Grid sx={SubBoardsContainerStyle} spacing={1} container>
                  {t.squamishSubBoards.map(sb => {
                    const index = subBoards?.findIndex((b) => b.description === sb.description);
                    return (
                      <Grid item xs={6} key={sb.description}>
                        <FormControl key={sb.description}>
                          <FormControlLabel
                            control={<Checkbox
                              checked={sb.isChecked}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                e.stopPropagation();

                                onChangeToggle(
                                  t.code,
                                  {
                                    ...t,
                                    squamishSubBoards: getUpdateSequence(index, e.target.checked)
                                  }
                                );
                              }}
                            />}
                            label={`${sb.description}${sb.sequence}`}
                            disabled={checkedMaxSubBoards && !checkedSubBoards?.find(b =>
                              b.description === sb.description)}
                          />
                        </FormControl>
                      </Grid>
                    )
                  })}
                </Grid> : null}
            </FormControl>
          ))
        }
      </Grid>
      <Grid item xs={1} sm={2} />
    </Grid>
  )
}